<template>
  <div class="mobile-home-section bg-gray-50">
    <!-- 轮播图区域 -->
    <div class="carousel-section bg-white">
      <div 
        class="carousel-container relative overflow-hidden rounded-b-2xl shadow-md"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
      >
        <div 
          class="carousel-wrapper"
          :style="{
            transform: `translateX(calc(-${currentSlide * 100}% + ${touchOffset}px))`,
            transition: isTransitioning ? 'transform 0.3s ease-out' : 'none'
          }"
        >
          <div 
            v-for="(banner, index) in banners" 
            :key="index"
            class="carousel-slide"
          >
            <img 
              :src="banner.image" 
              :alt="banner.title"
              class="w-full h-48 sm:h-64 md:h-72 object-cover"
              @load="handleImageLoad(index)"
              @error="handleImageError(index)"
            >
            <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 sm:p-6 z-10">
              <h3 class="text-white text-lg sm:text-xl font-bold mb-1">{{ banner.title }}</h3>
              <p class="text-white/90 text-sm sm:text-base">{{ banner.subtitle }}</p>
            </div>
          </div>
        </div>
        
        <!-- 轮播指示器 -->
        <div class="absolute bottom-2 left-0 right-0 flex justify-center space-x-2 z-10">
          <button 
            v-for="(banner, index) in banners" 
            :key="index"
            @click="goToSlide(index)"
            class="transition-all duration-300"
            :class="currentSlide === index ? 'w-6 h-2 bg-white' : 'w-2 h-2 bg-white/50'"
            :style="{ borderRadius: currentSlide === index ? '4px' : '50%' }"
          ></button>
        </div>
        
        <!-- 左右切换按钮 (平板显示) -->
        <button 
          @click="prevSlide" 
          class="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full items-center justify-center shadow-lg transition-all z-10"
        >
          <i class="fas fa-chevron-left text-gray-700"></i>
        </button>
        <button 
          @click="nextSlide" 
          class="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full items-center justify-center shadow-lg transition-all z-10"
        >
          <i class="fas fa-chevron-right text-gray-700"></i>
        </button>
      </div>
    </div>

    <!-- 系统公告区域 -->
    <div v-if="notices.length > 0" class="notice-section bg-white mx-4 mt-4 rounded-xl shadow-sm overflow-hidden">
      <div class="flex items-center px-4 py-3">
        <div class="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-orange-400 to-red-500 rounded-lg flex items-center justify-center">
          <i class="fas fa-bullhorn text-white text-lg blink-animation"></i>
        </div>
        <div class="flex-1 ml-3 overflow-hidden cursor-pointer" @click="goToNoticeDetail">
          <div class="notice-scroll">
            <transition name="slide-up" mode="out-in">
              <div :key="currentNotice" class="notice-item py-1">
                <p class="text-gray-700 text-sm sm:text-base truncate hover:text-primary transition-colors">
                  <span class="text-primary font-semibold mr-2 blink-animation">[公告]</span>
                  {{ noticesForDisplay[currentNotice].title }}
                </p>
              </div>
            </transition>
          </div>
        </div>
        <button 
          @click="showAllNotices" 
          class="flex-shrink-0 ml-2 text-gray-400 hover:text-primary transition-colors"
        >
          <i class="fas fa-angle-right text-xl"></i>
        </button>
      </div>
    </div>

    <!-- 功能菜单区域 -->
    <div class="menu-section px-4 py-6">
      <div class="grid grid-cols-2 gap-3 sm:gap-4">
        <!-- 培训考证 -->
        <div 
          @click="navigateTo('/courses')"
          class="menu-item menu-item-blue bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer overflow-hidden border border-gray-100"
        >
          <div class="p-5 sm:p-6 pb-6">
            <div class="flex flex-col items-center text-center space-y-3">
              <div class="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center shadow-md transform transition-transform hover:rotate-6">
                <i class="fas fa-graduation-cap text-white text-2xl sm:text-3xl"></i>
              </div>
              <div>
                <h3 class="text-gray-800 font-bold text-base sm:text-lg">培训课程</h3>
                <p class="text-gray-500 text-xs sm:text-sm mt-1">专业课程学习</p>
              </div>
            </div>
          </div>
          <div class="menu-item-accent"></div>
        </div>

        <!-- 题库练习 -->
        <div 
          @click="navigateTo('/quizzes')"
          class="menu-item menu-item-green bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer overflow-hidden border border-gray-100"
        >
          <div class="p-5 sm:p-6 pb-6">
            <div class="flex flex-col items-center text-center space-y-3">
              <div class="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center shadow-md transform transition-transform hover:rotate-6">
                <i class="fas fa-book text-white text-2xl sm:text-3xl"></i>
              </div>
              <div>
                <h3 class="text-gray-800 font-bold text-base sm:text-lg">练习题集</h3>
                <p class="text-gray-500 text-xs sm:text-sm mt-1">考试刷题测试</p>
              </div>
            </div>
          </div>
          <div class="menu-item-accent"></div>
        </div>

        <!-- 证书鉴别 -->
        <div 
          @click="handleCertificateClick"
          class="menu-item menu-item-purple bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer overflow-hidden border border-gray-100"
        >
          <div class="p-5 sm:p-6 pb-6">
            <div class="flex flex-col items-center text-center space-y-3">
              <div class="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl flex items-center justify-center shadow-md transform transition-transform hover:rotate-6">
                <i class="fas fa-certificate text-white text-2xl sm:text-3xl"></i>
              </div>
              <div>
                <h3 class="text-gray-800 font-bold text-base sm:text-lg">证书查询</h3>
                <p class="text-gray-500 text-xs sm:text-sm mt-1">证书真伪鉴别</p>
              </div>
            </div>
          </div>
          <div class="menu-item-accent"></div>
        </div>

        <!-- 就业创业 -->
        <div 
          @click="openEmploymentGuide"
          class="menu-item menu-item-orange bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer overflow-hidden border border-gray-100"
        >
          <div class="p-5 sm:p-6 pb-6">
            <div class="flex flex-col items-center text-center space-y-3">
              <div class="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl flex items-center justify-center shadow-md transform transition-transform hover:rotate-6">
                <i class="fas fa-briefcase text-white text-2xl sm:text-3xl"></i>
              </div>
              <div>
                <h3 class="text-gray-800 font-bold text-base sm:text-lg">就业指导</h3>
                <p class="text-gray-500 text-xs sm:text-sm mt-1">就业择业创业</p>
              </div>
            </div>
          </div>
          <div class="menu-item-accent"></div>
        </div>
      </div>
    </div>

    <!-- 快捷服务区域 (可选) -->
    <div class="quick-service-section px-4 pb-6">
      <div class="bg-gradient-to-br from-primary to-blue-600 rounded-xl shadow-lg p-6 text-white">
        <h3 class="text-lg sm:text-xl font-bold mb-3">快捷服务</h3>
        <div class="grid grid-cols-4 gap-3 sm:gap-4">
          <button
            @click="navigateTo('/exam-ticket')"
            class="flex flex-col items-center space-y-2 p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-all"
          >
            <i class="fas fa-id-card text-2xl"></i>
            <span class="text-xs sm:text-sm">准考证下载</span>
          </button>
          <button
            @click="navigateTo('/certificate-mailing')"
            class="flex flex-col items-center space-y-2 p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-all"
          >
            <i class="fas fa-shipping-fast text-2xl"></i>
            <span class="text-xs sm:text-sm">证书邮寄</span>
          </button>
          <button
            @click="openRegisterModal"
            class="flex flex-col items-center space-y-2 p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-all"
          >
            <i class="fas fa-user-plus text-2xl"></i>
            <span class="text-xs sm:text-sm">学员注册</span>
          </button>
          <button
            @click="showContactModal = true"
            class="flex flex-col items-center space-y-2 p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-all"
          >
            <i class="fas fa-headphones text-2xl"></i>
            <span class="text-xs sm:text-sm">联系我们</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 联系我们弹窗 -->
    <div 
      v-if="showContactModal" 
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      @click.self="showContactModal = false"
    >
      <div class="bg-white rounded-xl w-full max-w-sm overflow-hidden shadow-2xl">
        <!-- 弹窗头部 -->
        <div class="bg-primary text-white px-6 py-4 flex items-center justify-between">
          <h3 class="text-lg font-semibold">联系我们</h3>
          <button 
            @click="showContactModal = false" 
            class="text-white/80 hover:text-white transition-colors"
          >
            <i class="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>
        <!-- 弹窗内容 -->
        <div class="p-6">
          <ul class="space-y-4">
            <li class="text-gray-700 flex items-center">
              <span class="w-1.5 h-1.5 bg-primary rounded-full mr-3"></span>
              考场导航:河南新质机考研发中心
            </li>
            <li class="text-gray-700 flex items-center">
              <span class="w-1.5 h-1.5 bg-primary rounded-full mr-3"></span>
              等级认定:河南省康复教育学会
            </li>
            <li class="text-gray-700 flex items-center">
              <span class="w-1.5 h-1.5 bg-primary rounded-full mr-3"></span>
              专项考核:河南良知中医馆有限公司
            </li>
          </ul>
        </div>
        <!-- 弹窗底部 -->
        <div class="px-6 py-4 bg-gray-50 flex justify-end">
          <button 
            @click="showContactModal = false" 
            class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            确定
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { bannerAPI, noticeAPI } from '@/api/services'
import { getImageUrl } from '@/utils/imageUrl'

export default {
  name: 'MobileHomeSection',
  data() {
    return {
      // 轮播图数据
      currentSlide: 0,
      banners: [],
      isLoadingBanners: false,
      autoPlayTimer: null,
      touchStartX: 0,
      touchEndX: 0,
      touchOffset: 0,
      isTransitioning: false,
      isDragging: false,
      
      // 系统公告数据
      currentNotice: 0,
      notices: [],
      noticeTimer: null,
      
      // 联系我们弹窗
      showContactModal: false
    }
  },
  async mounted() {
    // 加载Banner数据
    this.fetchBanners()
    // 加载公告数据
    await this.fetchNotices()
    // 启动公告自动滚动（数据加载完成后）
    this.startNoticeScroll()
  },
  beforeDestroy() {
    // 清理定时器
    this.stopAutoPlay()
    this.stopNoticeScroll()
  },
  computed: {
    // 用于显示的公告列表（单条公告时复制一份以实现滚动效果）
    noticesForDisplay() {
      if (this.notices.length === 1) {
        // 只有一条公告时，复制两份以实现滚动效果
        return [...this.notices, ...this.notices]
      }
      return this.notices
    }
  },
  methods: {
    // 获取用于显示的公告列表
    getNoticesForDisplay() {
      if (this.notices.length === 1) {
        // 只有一条公告时，复制两份以实现滚动效果
        return [...this.notices, ...this.notices]
      }
      return this.notices
    },
    // 获取Banner数据
    async fetchBanners() {
      try {
        this.isLoadingBanners = true
        const response = await bannerAPI.getBanners()
        
        // 处理返回的数据，根据实际API响应结构调整
        if (response && Array.isArray(response)) {
          this.banners = response.map(banner => ({
            image: getImageUrl(banner.image),
            title: banner.title || '',
            subtitle: banner.subtitle || '',
            loaded: false
          }))
          
          // 数据加载完成后启动自动播放
          if (this.banners.length > 0) {
            this.startAutoPlay()
          }
        }
      } catch (error) {
        console.error('获取Banner数据失败:', error)
      } finally {
        this.isLoadingBanners = false
      }
    },

    // 获取公告数据
    async fetchNotices() {
      try {
        const response = await noticeAPI.getNotices({
          page: 1,
          pagesize: 10
        })
        
        // 处理返回的数据
        // 返回格式: { data: { total, per_page, current_page, last_page, data: [...] } }
        if (response && response.data && Array.isArray(response.data)) {
          this.notices = response.data.map(notice => ({
            id: notice.id,
            title: notice.title
          }))
        } else if (response && Array.isArray(response)) {
          // 兼容直接返回数组的情况
          this.notices = response.map(notice => ({
            id: notice.id,
            title: notice.title
          }))
        }
      } catch (error) {
        console.error('获取公告数据失败:', error)
      }
    },
    
    // 轮播图自动播放
    startAutoPlay() {
      // 清除之前的定时器
      this.stopAutoPlay()
      // 只在有banner数据时启动自动播放
      if (this.banners.length > 1) {
        this.autoPlayTimer = setInterval(() => {
          this.nextSlide()
        }, 4000)
      }
    },
    stopAutoPlay() {
      if (this.autoPlayTimer) {
        clearInterval(this.autoPlayTimer)
      }
    },
    nextSlide() {
      this.isTransitioning = true
      this.currentSlide = (this.currentSlide + 1) % this.banners.length
    },
    prevSlide() {
      this.isTransitioning = true
      this.currentSlide = (this.currentSlide - 1 + this.banners.length) % this.banners.length
    },
    goToSlide(index) {
      this.isTransitioning = true
      this.currentSlide = index
      // 重置自动播放
      this.stopAutoPlay()
      this.startAutoPlay()
    },
    
    // 触摸事件处理（支持滑动切换）
    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX
      this.isDragging = true
      this.isTransitioning = false
      this.stopAutoPlay()
    },
    handleTouchMove(e) {
      if (!this.isDragging) return
      
      // 阻止默认滚动行为，让滑动更流畅
      const touchCurrentX = e.touches[0].clientX
      const diff = touchCurrentX - this.touchStartX
      
      // 限制滑动范围，避免滑出边界太多
      if ((this.currentSlide === 0 && diff > 0) || 
          (this.currentSlide === this.banners.length - 1 && diff < 0)) {
        // 在边界时添加阻力效果
        this.touchOffset = diff * 0.3
      } else {
        this.touchOffset = diff
      }
      
      // 只在横向滑动较大时阻止默认行为
      if (Math.abs(diff) > 10) {
        e.preventDefault()
      }
    },
    handleTouchEnd(e) {
      if (!this.isDragging) return
      
      this.isDragging = false
      this.isTransitioning = true
      
      const diff = this.touchOffset
      const threshold = 50 // 滑动阈值
      
      if (Math.abs(diff) > threshold) {
        if (diff > 0 && this.currentSlide > 0) {
          // 向右滑动，显示上一张
          // 先重置偏移，避免与切换动画叠加
          this.touchOffset = 0
          this.prevSlide()
        } else if (diff < 0 && this.currentSlide < this.banners.length - 1) {
          // 向左滑动，显示下一张
          // 先重置偏移，避免与切换动画叠加
          this.touchOffset = 0
          this.nextSlide()
        } else {
          // 未达到切换条件，回弹
          this.touchOffset = 0
        }
      } else {
        // 滑动距离不够，回弹
        this.touchOffset = 0
      }
      
      this.startAutoPlay()
    },
    
    // 图片加载处理
    handleImageLoad(index) {
      this.banners[index].loaded = true
    },
    handleImageError(index) {
      console.error(`图片加载失败: ${this.banners[index].image}`)
      // 可以设置一个默认图片
      this.banners[index].image = 'https://via.placeholder.com/800x400?text=图片加载失败'
    },
    
    // 公告自动滚动
    startNoticeScroll() {
      if (this.notices.length > 0) {
        // 即使只有一条公告也启动滚动效果
        this.noticeTimer = setInterval(() => {
          this.currentNotice = (this.currentNotice + 1) % this.getNoticesForDisplay().length
        }, 3000)
      }
    },
    stopNoticeScroll() {
      if (this.noticeTimer) {
        clearInterval(this.noticeTimer)
      }
    },
    showAllNotices() {
      // 跳转到公告列表页面
      this.$router.push('/notices')
    },
    goToNoticeDetail() {
      // 跳转到当前公告的详情页面
      if (this.noticesForDisplay[this.currentNotice]) {
        // 获取实际的公告索引（处理单条公告复制的情况）
        const actualIndex = this.currentNotice % this.notices.length
        const noticeId = this.notices[actualIndex].id
        this.$router.push(`/notice/${noticeId}`)
      }
    },
    
    // 页面导航
    navigateTo(path) {
      this.$router.push(path)
    },

    // 就业指导 - 跳转外部网站
    openEmploymentGuide() {
      window.open('https://www.rongyuezhiya.com/', '_blank')
    },
    
    // 处理证书鉴别点击
    handleCertificateClick() {
      // 直接跳转到证书鉴别页面，不需要登录
      this.navigateTo('/certificate')
    },
    
    // 打开注册弹窗
    openRegisterModal() {
      this.$emit('open-register-modal')
    }
  }
}
</script>

<style scoped>
/* 轮播图容器和包装器 */
.carousel-container {
  width: 100%;
  touch-action: pan-y; /* 允许垂直滚动，但限制水平滚动 */
  user-select: none;
}

.carousel-wrapper {
  display: flex;
  width: 100%;
  will-change: transform;
}

.carousel-slide {
  flex-shrink: 0;
  width: 100%;
  position: relative;
  min-height: 12rem; /* 确保最小高度 */
}

.carousel-slide img {
  display: block;
  pointer-events: none; /* 防止图片拖拽 */
}

/* 公告滚动过渡效果 */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.4s ease;
}
.slide-up-enter {
  transform: translateY(20px);
  opacity: 0;
}
.slide-up-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

/* 菜单项样式优化 */
.menu-item {
  position: relative;
  background: linear-gradient(to bottom, #ffffff 0%, #fafafa 100%);
}

/* 底部装饰元素 */
.menu-item-accent {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, transparent 0%, currentColor 50%, transparent 100%);
  opacity: 0.6;
  transition: all 0.3s ease;
}

/* 底部光晕效果 */
.menu-item::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  width: 0%;
  height: 2px;
  background: currentColor;
  box-shadow: 0 0 12px currentColor;
  opacity: 0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 2px;
}

.menu-item:hover::after {
  width: 80%;
  opacity: 0.8;
}

.menu-item:hover .menu-item-accent {
  opacity: 0.9;
  height: 4px;
}

/* 不同颜色主题的卡片 */
.menu-item-blue {
  color: #3b82f6;
}

.menu-item-blue:hover {
  border-color: rgba(59, 130, 246, 0.3);
  box-shadow: 0 10px 25px -5px rgba(59, 130, 246, 0.2), 0 8px 10px -6px rgba(59, 130, 246, 0.1);
}

.menu-item-green {
  color: #10b981;
}

.menu-item-green:hover {
  border-color: rgba(16, 185, 129, 0.3);
  box-shadow: 0 10px 25px -5px rgba(16, 185, 129, 0.2), 0 8px 10px -6px rgba(16, 185, 129, 0.1);
}

.menu-item-purple {
  color: #8b5cf6;
}

.menu-item-purple:hover {
  border-color: rgba(139, 92, 246, 0.3);
  box-shadow: 0 10px 25px -5px rgba(139, 92, 246, 0.2), 0 8px 10px -6px rgba(139, 92, 246, 0.1);
}

.menu-item-orange {
  color: #f97316;
}

.menu-item-orange:hover {
  border-color: rgba(249, 115, 22, 0.3);
  box-shadow: 0 10px 25px -5px rgba(249, 115, 22, 0.2), 0 8px 10px -6px rgba(249, 115, 22, 0.1);
}

/* 闪动动画效果 */
@keyframes blink {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

.blink-animation {
  animation: blink 1.5s ease-in-out infinite;
}

/* 响应式调整 */
@media (min-width: 640px) {
  .mobile-home-section {
    max-width: 100%;
  }
}

@media (min-width: 768px) {
  .mobile-home-section {
    max-width: 768px;
    margin: 0 auto;
  }
  
  /* 平板上可以使用2x2更大的布局 */
  .menu-section .grid {
    gap: 1.5rem;
  }
}

</style>

